import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * PDP fulfillment messaging (presale / pre-order) synced to variant selection.
 */
class ProductFulfillmentDisplay extends Component {
  /** @type {Record<string, unknown> | null} */
  #data = null;

  connectedCallback() {
    const dataId = this.dataset.fulfillmentDataId;
    const dataEl = dataId ? document.getElementById(dataId) : null;
    if (dataEl?.textContent) {
      try {
        this.#data = JSON.parse(dataEl.textContent);
      } catch {
        this.#data = null;
      }
    }

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const initialVariantId = this.dataset.initialVariantId;
    if (initialVariantId) this.#applyVariant(String(initialVariantId));
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (variantId == null) return;
    this.#applyVariant(String(variantId));
    this.#updateAddToCartLabel(event);
  };

  /** @param {string} variantId */
  #applyVariant(variantId) {
    if (!this.#data) return;

    const preorder = this.#data.preorder === true;
    const presale = !preorder && this.#data.presale === true;
    const active = presale || preorder;

    const pdp = this.querySelector('[data-fulfillment-pdp]');
    if (pdp instanceof HTMLElement) {
      pdp.hidden = !active;
      pdp.classList.toggle('presale-fulfillment--presale', presale);
      pdp.classList.toggle('presale-fulfillment--preorder', preorder);

      const label = pdp.querySelector('[data-fulfillment-label]');
      const message = pdp.querySelector('[data-fulfillment-message]');
      const termsPreorder = pdp.querySelector('[data-fulfillment-terms-preorder]');
      const termsPresale = pdp.querySelector('[data-fulfillment-terms-presale]');

      if (label instanceof HTMLElement) {
        label.textContent = presale
          ? String(this.#data.labels?.presale || 'Presale')
          : String(this.#data.labels?.preorderHeading || 'PRE-ORDER ITEMS');
      }

      if (message instanceof HTMLElement) {
        message.textContent = this.#buildShipMessage(presale, preorder);
      }

      if (termsPreorder instanceof HTMLElement) {
        termsPreorder.hidden = !preorder;
      }
      if (termsPresale instanceof HTMLElement) {
        termsPresale.hidden = !presale;
      }
    }

    const propsRoot = this.closest('product-form-component')?.querySelector('[data-fulfillment-line-properties]');
    if (propsRoot instanceof HTMLElement) {
      propsRoot.querySelectorAll('[data-fulfillment-prop]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const mode = input.dataset.fulfillmentProp;
        const show = (presale && mode === 'presale') || (preorder && mode === 'preorder');
        input.disabled = !show;
        input.hidden = !show;
      });
    }
  }

  /** @param {boolean} presale @param {boolean} preorder */
  #buildShipMessage(presale, preorder) {
    if (!presale && !preorder) return '';

    const shipDisplay = String(this.#data?.shipDateDisplay || '').trim();
    const short = String(this.#data?.short || '').trim();
    const estimatedPrefix = String(this.#data?.labels?.estimatedShip || 'Estimated to ship from');

    if (shipDisplay !== '') {
      return `${estimatedPrefix} ${shipDisplay}.`;
    }
    if (short !== '') return short;
    if (presale) return String(this.#data?.labels?.presaleItem || 'Presale item.');
    return String(this.#data?.labels?.preorderItem || 'Pre-order item.');
  }

  /** @param {CustomEvent} event */
  #updateAddToCartLabel(event) {
    if (this.#data?.preorder !== true && this.#data?.presale === true) return;

    const preorder = this.#data?.preorder === true;

    const form = this.closest('product-form-component');
    const labelEl =
      form?.querySelector('.add-to-cart-text__content') ||
      form?.querySelector('.add-to-cart-text');
    if (!(labelEl instanceof HTMLElement)) return;

    if (preorder) {
      labelEl.textContent = String(this.#data?.labels?.preorderButton || 'Pre-Order');
      return;
    }

    const defaultLabel =
      (window.Theme && window.Theme.translations && window.Theme.translations.add_to_cart) || 'Add to Bag';
    const isAvailable = !!event.detail?.resource?.available;
    if (!isAvailable) {
      labelEl.textContent =
        (window.Theme && window.Theme.translations && window.Theme.translations.sold_out) || 'Sold out';
      return;
    }
    labelEl.textContent = defaultLabel;
  }
}

if (!customElements.get('product-fulfillment-display')) {
  customElements.define('product-fulfillment-display', ProductFulfillmentDisplay);
}
